import React, { useEffect, useRef, useState } from "react";
import { X, Gift, Loader2, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { joinWaitlist } from "@/lib/api";

const DISMISSED_KEY = "spectraloom_waitlist_dismissed";
const JOINED_KEY = "spectraloom_waitlist_joined";

/**
 * Floating pre-launch waitlist bar.
 *
 * Rendered at the very top of the app, above InAppBrowserBanner. It offers
 * a "10% off first purchase" incentive in exchange for the visitor's email.
 * Behaviour:
 *   - The bar is a normal DOM element in the flow (top: 0, sticky) so it
 *     never covers other content; the rest of the page shifts down.
 *   - Auto-hides forever (via localStorage) once the user either
 *     dismisses with ×, or successfully submits their email.
 *   - Copy switches based on the current i18n language (auto-detected
 *     from `navigator.language` on first visit in `i18n/index.js`).
 */
export default function WaitlistBar() {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const successTimer = useRef(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISSED_KEY) === "1") return;
      if (localStorage.getItem(JOINED_KEY) === "1") return;
    } catch {
      /* localStorage disabled → show it anyway */
    }
    setVisible(true);
  }, []);

  useEffect(() => () => clearTimeout(successTimer.current), []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError(t("waitlist.err_consent"));
      return;
    }
    const trimmed = email.trim().toLowerCase();
    // Cheap client-side sanity check · real validation happens server-side.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setError(t("waitlist.err_email"));
      return;
    }
    setSubmitting(true);
    try {
      await joinWaitlist(trimmed, true, i18n.language);
      try {
        localStorage.setItem(JOINED_KEY, "1");
      } catch {
        /* ignore */
      }
      setSuccess(true);
      // Keep the confirmation on screen for a few seconds, then collapse.
      successTimer.current = setTimeout(() => setVisible(false), 4500);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "";
      setError(
        detail === "invalid_email"
          ? t("waitlist.err_email")
          : detail === "disposable_email"
          ? t("waitlist.err_disposable")
          : t("waitlist.err_generic")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="sticky top-0 left-0 right-0 z-[97] border-b border-white/10 bg-gradient-to-r from-blue-600/95 via-violet-600/95 to-fuchsia-600/95 text-white shadow-lg backdrop-blur-sm"
      data-testid="waitlist-bar"
      role="region"
      aria-label={t("waitlist.aria")}
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-start gap-2 md:flex-1 min-w-0">
          <Gift size={18} className="shrink-0 mt-0.5 text-white/90" />
          <p className="text-sm leading-snug">{t("waitlist.pitch")}</p>
        </div>

        {success ? (
          <div
            data-testid="waitlist-success"
            className="inline-flex items-center gap-2 rounded-lg bg-white/15 border border-white/25 px-3 py-2 text-sm font-medium"
          >
            <Check size={16} className="text-emerald-200" />
            <span>{t("waitlist.success")}</span>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="flex flex-col sm:flex-row sm:items-center gap-2 md:shrink-0"
            data-testid="waitlist-form"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                type="email"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("waitlist.email_placeholder")}
                disabled={submitting}
                className="w-full sm:w-64 rounded-md bg-white/95 text-slate-900 placeholder:text-slate-500 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white/60"
                data-testid="waitlist-email-input"
                aria-label={t("waitlist.email_placeholder")}
              />
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-900/90 hover:bg-slate-900 text-white text-sm font-semibold px-4 py-2 border border-white/25 disabled:opacity-70"
                data-testid="waitlist-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {t("waitlist.submitting")}
                  </>
                ) : (
                  t("waitlist.cta")
                )}
              </button>
            </div>
            <label className="flex items-start gap-2 text-xs leading-snug text-white/90 max-w-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={submitting}
                className="mt-0.5 h-4 w-4 shrink-0 accent-fuchsia-300 cursor-pointer"
                data-testid="waitlist-consent"
              />
              <span>{t("waitlist.consent")}</span>
            </label>
          </form>
        )}

        <button
          type="button"
          onClick={dismiss}
          aria-label={t("waitlist.dismiss")}
          className="shrink-0 -mr-1 p-1.5 rounded-md hover:bg-white/15 text-white/90 hover:text-white self-start md:self-center"
          data-testid="waitlist-dismiss"
        >
          <X size={16} />
        </button>
      </div>
      {error && !success && (
        <div
          className="max-w-6xl mx-auto px-4 pb-2 -mt-1 text-xs text-amber-100"
          data-testid="waitlist-error"
          role="alert"
        >
          {error}
        </div>
      )}
    </div>
  );
}
